import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { country } from "@flint/api-contract";
import { useNavigate } from "@tanstack/react-router";
import { Button, toast } from "@flint/ui";
import { ProductImagesField } from "./product-images-field.tsx";
import { NewOptionDialog, type OptionKind } from "./new-option-dialog.tsx";
import {
  optionsForDialog,
  useCatalogOptions,
  useSubCategoryReset,
} from "./use-catalog-options.ts";
import { HSN_BLOCKER } from "./listing-status.ts";
import { ProductWizardStepper } from "./product-wizard-stepper.tsx";
import { ProductReviewStep, FIELD_STEP } from "./product-review-step.tsx";
import { ProductPreviewRail } from "./product-preview-rail.tsx";
import { AttributeBuilder } from "./attribute-builder.tsx";
import { VariantsField, variantsToWire } from "./variants-field.tsx";
import {
  completeAxes,
  variantAttributesFormSchema,
  variantsFormSchema,
} from "./variant-form.ts";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  TextField,
  SelectField,
} from "@flint/ui/forms";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { WarehouseCreateDialog } from "../../inventory/warehouses/warehouse-create-dialog.tsx";
import { storeErrorMessage } from "../error-message.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { StoreFormSection, StoreFormRow, AddOptionButton } from "./store-form-section.tsx";
import {
  CARE_OPTIONS,
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY_OF_ORIGIN,
  CARE_VALUES,
  TEXTAREA_CLASS,
  buildAttributes,
} from "./product-form-constants.ts";
import { ProductPackFields } from "./product-pack-fields.tsx";
import { SkuField } from "./sku-field.tsx";
import { perPiecePaise } from "../price-math.ts";
import {
  MAX_PACKS,
  effectivePiecesPerPack,
  entryPiecesOnHand,
  pricingEntryFormSchema,
  usableEntries,
} from "./size-ratio.ts";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(255),
  // Marked required to match the wireframe — a wholesale SKU without a code
  // can't be referenced on an order. The API itself accepts it as optional.
  sku: z.string().trim().min(1, "SKU is required").max(32),
  hsn: z.string().trim().max(8),
  // Marked required on the label, so it has to be required in the schema —
  // a bare `.max()` let the form submit with the picker still on its
  // placeholder, which is what "Category *" promised it wouldn't.
  category: z.string().trim().min(1, "Category is required").max(48),
  // Optional where the parent is required: plenty of catalogues are one level
  // deep, and forcing a child would have operators inventing filler terms.
  subCategory: z.string().trim().max(48),
  brand: z.string().trim().max(96),
  // Closed set (blank = nobody has said), validated against the same enum the
  // server uses — so the picker can't offer a value the API would refuse.
  countryOfOrigin: country.or(z.literal("")),
  description: z.string().trim().max(500),
  // Free text now, so guard the range here — `taxRateBps` is capped at 10_000
  // (100%) on the wire, and an unchecked entry would come back a 400.
  taxPct: z
    .string()
    .trim()
    .refine((v) => v === "" || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100), {
      message: "Enter a GST rate between 0 and 100",
    }),
  // Kept only for a listing saved in the legacy positional format, which can't
  // become an entry but still has a real piece count. @see ProductPackFields
  piecesPerPack: z.string().trim(),
  sizeRatio: z.string().trim().max(64),
  // The variant matrix — fragments shared with the edit page (@see variant-form.ts).
  variantAttributes: variantAttributesFormSchema,
  variants: variantsFormSchema,
  // One entry per way to buy the product — kind, composition, price, discount
  // and MOQ all vary between them, so each carries its own. `pricingEntries[0]`
  // is the primary the server mirrors into the single-value columns (ADR-0051).
  // The element schema is shared with the edit form and the picker's own type:
  // a private copy here is what dropped `packId` on every save (#196).
  pricingEntries: z.array(pricingEntryFormSchema).max(MAX_PACKS),
  shortDesc: z.string().trim().max(500),
  longDesc: z.string().trim().max(4000),
  material: z.string().trim().max(120),
  gsm: z.string().trim().max(120),
  colour: z.string().trim().max(120),
  collection: z.string().trim().max(120),
  // Closed set (blank = not specified). Free text before — see CARE_VALUES.
  care: z.enum(CARE_VALUES).or(z.literal("")),
  // Opening stock. Both blank = don't post an opening balance at all, which is
  // the common case; a quantity without a warehouse is the one combination
  // that can't be honoured, so it's rejected rather than silently dropped.
  openingWarehouseId: z.string().trim(),
  openingQty: z.string().trim(),
});
type Values = z.infer<typeof schema>;

/**
 * Copy for the one rule pricing has: **publishing** needs a price, saving a
 * draft does not (spec 13, R5/AC9). Frozen wording — the same sentence the
 * server raises from `publishListing`, so the operator reads one rule whichever
 * control refused them.
 */
const NEEDS_PRICE = "Add at least one pack price";

/**
 * The creation wizard's steps (#272, UX-review finding 8) — the same seven
 * section cards as before plus a Review & Publish step, shown one at a time.
 * The step order is the data-flow order the page always had (axes → variants
 * → bundles, #247). The EDIT page runs the same wizard (user decision
 * 2026-08-13) minus the Inventory step — see EDIT_WIZARD_STEPS there.
 */
const WIZARD_STEPS = [
  {
    key: "basic",
    label: "Basic information",
    description:
      "Tell buyers what this product is — it appears across your catalog, quotes and orders.",
  },
  { key: "images", label: "Images", description: "Up to 8 photos; the first is the primary." },
  {
    key: "attributes",
    label: "Attributes",
    description: "Define the option axes this product comes in — Size, Color, Material…",
  },
  {
    key: "variants",
    label: "Variants",
    description: "Every attribute combination as a row you can fine-tune.",
  },
  // Inventory BEFORE Bundle Builder (user decision 2026-08-13): declare the
  // stock you hold first, then compose bundles out of it — the coverage rule
  // (bundles ≤ opening pieces) reads forward instead of backward.
  {
    key: "inventory",
    label: "Inventory",
    description: "Opening stock on hand, posted when the product is created.",
  },
  {
    key: "bundles",
    label: "Bundle Builder",
    description: "Compose and price the fixed packages a dealer buys.",
  },
  {
    key: "merchandising",
    label: "Merchandising",
    description: "Copy and attributes shown on the line sheet and product page.",
  },
  {
    key: "review",
    label: "Review & Publish",
    description: "Check everything, pick the status, and create.",
  },
] as const;

/** Fields each step validates on Next — full validation still runs at submit. */
const STEP_FIELDS: (keyof Values)[][] = [
  ["name", "sku", "hsn", "category", "subCategory", "collection", "description"],
  [],
  ["variantAttributes"],
  ["variants"],
  ["openingWarehouseId", "openingQty"],
  ["pricingEntries", "taxPct", "piecesPerPack", "sizeRatio"],
  ["shortDesc", "longDesc", "brand", "countryOfOrigin", "material", "gsm", "colour", "care"],
];

/**
 * Is there something to price this product at?
 *
 * One priced entry is enough — not `pieces > 0`, because the size grid is
 * optional and an entry may be priced without a composition. The escape hatch
 * is a listing saved in the old positional format, which can't become an entry
 * but still has a real piece count.
 *
 * Deliberately **not** a schema refinement. It used to be, which put it in the
 * resolver — and the resolver runs for every button, so "Save as draft" was
 * refused on a priceless product and fired no request at all, against AC9. The
 * check belongs where the difference is known: the publish path.
 */
function hasPricing(v: Values): boolean {
  if (usableEntries(v.pricingEntries).length > 0) return true;
  return effectivePiecesPerPack(v.sizeRatio, v.piecesPerPack) > 0;
}

/**
 * Opening stock must COVER the bundles: the bundles' pieces
 * (Σ composition × packs on hand) are claimed to physically exist, so an
 * opening count below that is a contradiction the operator should resolve
 * before saving. Returns the required minimum when short, null when fine.
 * Blank opening qty stays legal — blank means "post nothing", not zero.
 */
function openingStockShortfall(v: Values): number | null {
  const qty = v.openingQty.trim() ? Number(v.openingQty) : 0;
  if (qty <= 0) return null;
  const bundlePieces = v.pricingEntries.reduce((sum, e) => sum + entryPiecesOnHand(e), 0);
  return bundlePieces > 0 && qty < bundlePieces ? bundlePieces : null;
}

/**
 * Store admin — New product (Commerce, V1.0.1 M2.2). react-hook-form + @flint/ui.
 * @see docs/ecom/v1.0.1-plan.md §5.2
 */
export function ProductNewPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const create = trpc.storeCatalog.create.useMutation();
  const publish = trpc.storeCatalog.publish.useMutation();
  const busy = create.isPending || publish.isPending;
  const [images, setImages] = useState<string[]>([]);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "", sku: "", hsn: "", category: "", subCategory: "", brand: "", description: "",
      // Defaulted here, not in the column: on a *new* product India is the safe
      // assumption for this customer base, but a saved null has to keep meaning
      // "nobody has said". @see DEFAULT_COUNTRY_OF_ORIGIN
      countryOfOrigin: DEFAULT_COUNTRY_OF_ORIGIN,
      taxPct: "5",
      // `piecesPerPack` starts blank: it only exists for a legacy positional
      // run, and a pre-filled count would mask an empty entry list.
      piecesPerPack: "", sizeRatio: "", pricingEntries: [],
      variantAttributes: [], variants: [],
      shortDesc: "", longDesc: "", material: "", gsm: "", colour: "", collection: "", care: "",
      openingWarehouseId: "", openingQty: "",
    },
  });
  /**
   * What the product will be created as — the deferred sibling of the edit
   * form's immediate-apply control. Nothing exists to transition yet, so the
   * selection is intent, applied at save: Draft creates a draft, Published
   * creates then fires the `publish` transition. Archived is unavailable —
   * a product that doesn't exist yet has nothing to archive.
   *
   * The one action button follows this selection, so the two can't disagree
   * (the previous two-button row let "Save as draft" contradict nothing; a
   * status selector beside it would have).
   */
  const [createAs, setCreateAs] = useState<"draft" | "published">("draft");
  // Wizard position (#272). Every section stays MOUNTED (visibility is CSS):
  // unmounting would lose component-internal state react-hook-form doesn't
  // hold — the variant matrix's session tombstones above all.
  const [step, setStep] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);
  const goToStep = useCallback((i: number) => {
    setStep(i);
    setMaxVisited((m) => Math.max(m, i));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const nextStep = async () => {
    const fields = STEP_FIELDS[step] ?? [];
    // Validate only this step's fields so errors surface where they're made;
    // an empty list (Images — local state, no schema) always passes. The
    // opening-stock coverage rule is deliberately NOT checked here — it fires
    // in the bundle dialog at save (user decision 2026-08-13) and again at
    // final save, which covers an opening qty lowered after the bundles exist.
    const ok = fields.length === 0 || (await form.trigger(fields));
    if (ok) {
      goToStep(step + 1);
      return;
    }
    // Inline errors alone can sit below the fold — say it out loud too.
    const firstError = fields
      .map((f) => form.getFieldState(f).error?.message)
      .find((m): m is string => Boolean(m));
    toast.error(
      "Fix the highlighted fields to continue",
      firstError ? { description: firstError } : undefined,
    );
  };
  // Warehouses come from the Inventory module — one owner for the list.
  const warehouses = trpc.warehouses.list.useQuery({ includeInactive: false });
  const warehouseOptions = (warehouses.data?.warehouses ?? []).map((w) => ({
    label: w.code ? `${w.name} · ${w.code}` : w.name,
    value: w.id,
  }));
  // Inline warehouse creation (#268): the trigger is gated on the same
  // permission `warehouses.create` enforces — Sales Manager holds only
  // `inventory.view` (deliberate) and keeps the go-to-Inventory copy.
  const canManageInventory = usePermission("inventory.manage");
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  // Same deferred-select rule as pendingSelect below: the new warehouse can
  // only be committed to the form once the invalidated list contains it.
  const [pendingWarehouseId, setPendingWarehouseId] = useState<string | null>(null);
  useEffect(() => {
    if (pendingWarehouseId && warehouseOptions.some((o) => o.value === pendingWarehouseId)) {
      form.setValue("openingWarehouseId", pendingWarehouseId, { shouldDirty: true });
      setPendingWarehouseId(null);
    }
  }, [pendingWarehouseId, warehouseOptions, form]);
  // Picker vocabulary comes from the catalog itself — neither category nor
  // collection has a master table, so there is no fixed list to hardcode.
  const options = useCatalogOptions();
  const [optionDialog, setOptionDialog] = useState<OptionKind | null>(null);

  // The child picker's list, its placeholder and its dialog all key off the
  // parent the operator has selected right now.
  const category = form.watch("category");
  const subCategoryOptions = options.subCategoriesFor(category);
  useSubCategoryReset(
    category,
    useCallback(() => form.setValue("subCategory", "", { shouldDirty: true }), [form]),
  );

  // Selecting a just-added value has to wait for it to exist as an option:
  // Radix Select falls back to the placeholder when the value matches no item,
  // and won't re-derive the label when the list catches up.
  const [pendingSelect, setPendingSelect] = useState<{ kind: OptionKind; name: string } | null>(
    null,
  );
  useEffect(() => {
    if (pendingSelect && options.has(pendingSelect.kind, pendingSelect.name, category)) {
      form.setValue(pendingSelect.kind, pendingSelect.name, { shouldDirty: true });
      setPendingSelect(null);
    }
  }, [pendingSelect, options, form, category]);

  async function onSubmit(v: Values, publishAfter: boolean) {
    const entries = usableEntries(v.pricingEntries);
    const primary = entries[0];
    const pieces = primary?.pieces || effectivePiecesPerPack(v.sizeRatio, v.piecesPerPack);
    // The item rate is banked per piece whatever the primary entry is priced
    // in, because invoice line rates and the GST slab both read
    // `default_rate_paise` that way. A pack divides; a piece is already there.
    const ratePaise = primary
      ? primary.kind === "pack"
        ? perPiecePaise(BigInt(primary.pricePaise || "0"), pieces)
        : BigInt(primary.pricePaise || "0")
      : 0n;
    // Publishing is the only thing a price is required for (R5/AC9). A draft
    // saves at rate 0 — `basePricePaise` is a non-negative integer on the wire,
    // and `publishListing` is the server floor that stops a priceless draft
    // going live, so nothing here has to guard the store front.
    if (publishAfter && (ratePaise <= 0n || !hasPricing(v))) {
      form.setError("pricingEntries", { message: NEEDS_PRICE });
      toast.error("Can't publish yet", { description: NEEDS_PRICE });
      goToStep(FIELD_STEP.pricingEntries!);
      return;
    }
    // The other publish floor. Checked *before* the create call because the
    // server enforces it in `publishListing`, after the product exists — so
    // without this, "create as published" with a blank HSN would create the
    // product and then refuse the publish, leaving a draft behind a failure
    // toast and a duplicate-SKU error on the natural retry.
    if (publishAfter && !v.hsn.trim()) {
      form.setError("hsn", { message: "Required before publishing" });
      toast.error("Can't publish yet", { description: HSN_BLOCKER });
      goToStep(FIELD_STEP.hsn!);
      return;
    }
    // The stock ledger counts pieces. With entries a product can be both piece-
    // and pack-sold, so there is no single unit to count opening stock in — it
    // is entered in pieces, which is what `recordOpeningBalance` posts anyway.
    const openingQty = v.openingQty.trim() ? Number(v.openingQty) : 0;
    // A quantity with no warehouse can't be posted anywhere. Say so rather
    // than dropping the number the operator typed.
    if (openingQty > 0 && !v.openingWarehouseId) {
      form.setError("openingWarehouseId", { message: "Pick a warehouse for the opening stock" });
      toast.error("Couldn't save the product", {
        description: "Pick a warehouse for the opening stock.",
      });
      goToStep(FIELD_STEP.openingWarehouseId!);
      return;
    }
    // Same coverage rule as the Bundle Builder step's Next — re-checked here
    // because either number can change after that step was passed.
    const shortfall = openingStockShortfall(v);
    if (shortfall !== null) {
      form.setError("pricingEntries", {
        message: `Bundles total ${shortfall} pcs but opening stock is ${v.openingQty.trim()} pcs`,
      });
      toast.error("Bundles exceed the opening stock", {
        description: `Your bundles account for ${shortfall} pcs but opening stock is ${v.openingQty.trim()} pcs — reduce the bundles or raise the opening stock.`,
      });
      goToStep(FIELD_STEP.pricingEntries!);
      return;
    }
    const attributes = buildAttributes(v);
    try {
      const res = await create.mutateAsync({
        name: v.name.trim(),
        basePricePaise: ratePaise.toString(),
        // `soldBy` and `discountBps` are mirrored from the primary entry
        // server-side; these are the fallbacks for a caller sending no entries.
        // Pack, not piece: since spec 13 a garment is only sold by the pack, and
        // the only way to reach this fallback is a draft saved before it has any
        // pricing at all — which will be priced by the pack too.
        soldBy: primary?.kind ?? "pack",
        discountBps: primary?.discountBps ?? 0,
        ...(v.sku ? { sku: v.sku } : {}),
        ...(v.hsn ? { hsn: v.hsn } : {}),
        ...(v.category ? { category: v.category } : {}),
        ...(v.subCategory ? { subCategory: v.subCategory } : {}),
        ...(v.brand ? { brand: v.brand } : {}),
        ...(v.countryOfOrigin ? { countryOfOrigin: v.countryOfOrigin } : {}),
        // Axes with no name or no values are unfinished builder rows — dropped
        // here, where the wire requires complete ones.
        ...(completeAxes(v.variantAttributes).length
          ? { variantAttributes: completeAxes(v.variantAttributes) }
          : {}),
        ...(v.variants.length ? { variants: variantsToWire(v.variants) } : {}),
        ...(v.description ? { description: v.description } : {}),
        ...(v.taxPct !== "" ? { taxRateBps: Math.round(Number(v.taxPct) * 100) } : {}),
        ...(v.shortDesc ? { shortDesc: v.shortDesc } : {}),
        ...(v.longDesc ? { longDesc: v.longDesc } : {}),
        ...(Object.keys(attributes).length ? { attributes } : {}),
        collectionId: v.collection || null,
        ...(images.length ? { images } : {}),
        // The entries are authoritative and the server mirrors
        // `pricingEntries[0]` into the single-value columns (ADR-0051), so
        // `sizeRatio` / `piecesPerPack` are only sent as the legacy fallback for
        // a positional run that couldn't become an entry.
        ...(entries.length > 0
          ? { pricingEntries: entries }
          : {
              ...(pieces > 0 ? { piecesPerPack: pieces } : {}),
              ...(v.sizeRatio.trim() ? { sizeRatio: v.sizeRatio.trim() } : {}),
            }),
        // MOQ lives on the entry now; the column mirrors the primary.
        ...(primary?.moq ? { moq: primary.moq } : {}),
        ...(openingQty > 0 && v.openingWarehouseId
          ? { openingStock: { warehouseId: v.openingWarehouseId, quantity: openingQty } }
          : {}),
      });
      if (publishAfter) await publish.mutateAsync({ listingId: res.listingId });
      await Promise.all([
        utils.storeCatalog.list.invalidate(),
        utils.storeCatalog.facets.invalidate(),
        utils.storeCatalog.counts.invalidate(),
      ]);
      toast.success(publishAfter ? "Product created & published" : "Product saved as draft");
      await navigate({ to: "/store/admin/products" });
    } catch (err) {
      toast.error("Couldn't save the product", {
        description: storeErrorMessage(err),
      });
    }
  }

  const submit = (publishAfter: boolean) =>
    form.handleSubmit(
      (v) => onSubmit(v, publishAfter),
      // Schema errors on submit: a failing field may live on ANOTHER step, so
      // an inline message alone would be invisible — toast it and jump to the
      // step that owns the first offender.
      (errs) => {
        const [field, err] = Object.entries(errs)[0] ?? [];
        toast.error("Couldn't save the product", {
          description:
            (err as { message?: string } | undefined)?.message ?? "Fix the highlighted fields.",
        });
        if (field && FIELD_STEP[field] !== undefined) goToStep(FIELD_STEP[field]!);
      },
    )();

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Products", to: "/store/admin/products" },
        { label: "New product" },
      ]}
    >
      {/* Grid width to match the widened wizard below (#272) — header and
          cards share one left edge, so the back arrow starts the page. */}
      <StorePageColumn variant="grid">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BackButton fallbackTo="/store/admin/products" label="Back to products" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
                New product
              </h1>
              <p className="mt-1.5 text-sm text-muted">Add a SKU to the wholesale catalog.</p>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Only on the final step — mid-wizard the action lives in the
                Review card; a header button on every step drew clicks before
                the operator had seen the later steps. */}
            {step === WIZARD_STEPS.length - 1 ? (
              <Button
                intent="primary"
                size="md"
                disabled={busy}
                onClick={() => submit(createAs === "published")}
              >
                {createAs === "published" ? <>Create &amp; publish</> : <>Save as draft</>}
              </Button>
            ) : null}
          </div>
        </div>
      </StorePageColumn>

      <Form {...form}>
        {/* Wider than the old single-page cap (1024px): the wizard shows one
            card at a time, so the page can afford the room — and the stepper
            needs the width to lay all eight steps on one line (#272). */}
        <form
          className="mx-auto mt-5 w-full max-w-7xl"
          onSubmit={(e) => e.preventDefault()}
        >
          {/* The TradeCore artifact's wizard chrome, ink-themed: vertical
              setup rail left, step content centre, live preview right. */}
          <div className="flex items-start gap-6">
            <aside className="sticky top-4 hidden w-56 shrink-0 lg:block">
              <ProductWizardStepper
                steps={WIZARD_STEPS}
                current={step}
                maxVisited={maxVisited}
                onGo={goToStep}
              />
            </aside>

            <div className="min-w-0 flex-1">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Step {step + 1} of {WIZARD_STEPS.length}
                </p>
                <h2 className="mt-1 text-xl font-bold tracking-tight text-ink">
                  {WIZARD_STEPS[step]!.label}
                </h2>
                <p className="mt-1 text-sm text-muted">{WIZARD_STEPS[step]!.description}</p>
              </div>
          {/* One card PER SECTION (#265), one VISIBLE at a time (#272) — the
              hidden wrappers keep everything mounted so no state is lost. */}
          <div className="flex flex-col gap-4">
            <div className={step === 0 ? undefined : "hidden"}>
            <StoreFormSection
              title="Identity"
              help="Name, SKU, HSN, category — how the product is discoverable in lists and reports."
            >
              <StoreFormRow single>
                <TextField name="name" label="Product name *" placeholder="Cotton Round-Neck Tee — Ecru" />
              </StoreFormRow>
              <StoreFormRow>
                <SkuField
                  name="sku"
                  label="SKU *"
                  placeholder="SKU-CT-RN-ECR"
                  helper="Unique; immutable once used on an order. Upper-cased as you type."
                />
                <TextField name="hsn" label="HSN code" placeholder="6109" />
              </StoreFormRow>
              <StoreFormRow cols={3}>
                <div>
                  <SelectField
                    name="category"
                    label="Category *"
                    options={options.categories}
                    placeholder={options.placeholderFor("category")}
                  />
                  <AddOptionButton label="New category" onClick={() => setOptionDialog("category")} />
                </div>
                <div>
                  {/* Disabled until there's a parent to file it under — a
                      sub-category exists only as a pairing with a category. */}
                  <SelectField
                    name="subCategory"
                    label="Sub-category"
                    options={subCategoryOptions}
                    placeholder={options.placeholderFor("subCategory", category)}
                    disabled={!category}
                    // Only offered when there is something to clear back from.
                    // `noneLabel` makes an empty value select the "none" item,
                    // which would otherwise hide the placeholder that tells the
                    // operator to pick a category first (spec 45 AC2).
                    {...(subCategoryOptions.length ? { noneLabel: "Not specified" } : {})}
                  />
                  {category ? (
                    <AddOptionButton
                      label="New sub-category"
                      onClick={() => setOptionDialog("subCategory")}
                    />
                  ) : null}
                </div>
                <div>
                  {/* Adds only — see the note on the edit page. A style can sit
                      in several line sheets (spec 15 R5). */}
                  <SelectField
                    name="collection"
                    label="Add to collection"
                    options={options.collections}
                    placeholder={options.placeholderFor("collection")}
                  />
                </div>
              </StoreFormRow>
              <StoreFormRow single>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          value={field.value ?? ""}
                          rows={3}
                          placeholder="Short marketing copy shown on line sheets and quotes."
                          className={TEXTAREA_CLASS}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </StoreFormRow>
            </StoreFormSection>
            </div>

            <div className={step === 1 ? undefined : "hidden"}>
            <StoreFormSection
              title="Images"
              help="Up to 8. The first image is the primary one shown on line sheets."
            >
              <ProductImagesField value={images} onChange={setImages} />
            </StoreFormSection>
            </div>

            {/* Attributes and Variants sit BEFORE Bundle Builder — the TradeCore
                mockup's step order, and a hard dependency since #247: the bundle
                dialog composes every bundle from the Size/Color axes defined here
                and has no picker to fall back on. */}
            <div className={step === 2 ? undefined : "hidden"}>
            <StoreFormSection
              title="Attributes"
              help="Define the options this product comes in — every combination of values becomes a variant below. Additive to pack pricing; packs stay the unit of sale."
            >
              <FormField
                control={form.control}
                name="variantAttributes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <AttributeBuilder value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </StoreFormSection>
            </div>

            <div className={step === 3 ? undefined : "hidden"}>
            <StoreFormSection
              title="Variants"
              help="Every attribute combination, generated automatically — what the product comes in. Fine-tune SKU, barcode and weight; stock is derived from the bundles below."
            >
              <FormField
                control={form.control}
                name="variants"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <VariantsField
                        value={field.value}
                        onChange={field.onChange}
                        attributes={form.watch("variantAttributes")}
                        baseSku={form.watch("sku")}
                        pricingEntries={form.watch("pricingEntries") ?? []}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </StoreFormSection>
            </div>

            {/* Bundle Builder is its own card, separate from Variants (#265,
                UX-review finding 1): variants describe the product, bundles are
                the fixed packages a dealer buys — one blob read as neither.
                NOTE: source order ≠ step order — only one section is visible
                at a time, and Inventory (below) is step 4, this is step 5. */}
            <div className={step === 5 ? undefined : "hidden"}>
            <StoreFormSection
              title="Bundle Builder"
              help="Compose the fixed packages a dealer buys — pick variants, set quantities per bundle, price each one. A product can offer several bundles."
            >
              {/* GST first: it applies to every bundle, and each card shows its
                  own incl-GST figure, so the rate has to be answerable before
                  the list means anything. */}
              <StoreFormRow>
                <TextField
                  name="taxPct"
                  label="GST (%)"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  placeholder="5"
                  helper="Any rate — the usual slabs are 0, 5, 12, 18 and 28."
                />
              </StoreFormRow>

              <ProductPackFields />
            </StoreFormSection>
            </div>

            <div className={step === 4 ? undefined : "hidden"}>
            <StoreFormSection
              title="Inventory"
              {...(warehouseOptions.length === 0 ? { note: "No warehouses yet" } : {})}
              help="Opening stock on hand, posted to the stock ledger when the product is created. Leave blank if you're not holding any yet."
            >
              {warehouseOptions.length === 0 ? (
                warehouses.isLoading ? (
                  <p className="text-sm text-muted">Loading warehouses…</p>
                ) : canManageInventory ? (
                  // The dead-end the UX review called out (#268, finding 4):
                  // create the warehouse here instead of a trip to Inventory.
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-sm text-muted">No warehouses yet.</p>
                    <Button
                      intent="secondary"
                      size="sm"
                      type="button"
                      onClick={() => setWarehouseDialogOpen(true)}
                    >
                      New warehouse
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted">
                    Add a warehouse in the Inventory module before recording opening stock.
                  </p>
                )
              ) : (
                <StoreFormRow>
                  {/* Button in the same cell, directly under the select — the
                      category/collection pattern. */}
                  <div>
                    <SelectField
                      name="openingWarehouseId"
                      label="Warehouse"
                      options={warehouseOptions}
                      placeholder="Select…"
                    />
                    {canManageInventory ? (
                      <AddOptionButton
                        label="New warehouse"
                        onClick={() => setWarehouseDialogOpen(true)}
                      />
                    ) : null}
                  </div>
                  {/* Always pieces. A product can now be sold both loose and in
                      packs (ADR-0051), so there is no single unit of sale to
                      count in — and the ledger was always pieces regardless. */}
                  <TextField
                    name="openingQty"
                    label="Opening qty (pieces)"
                    placeholder="0"
                    inputMode="numeric"
                    helper="Valued at the primary entry's per-piece rate. Later movement goes through Inventory."
                  />
                </StoreFormRow>
              )}
            </StoreFormSection>
            </div>

            <div className={step === 6 ? undefined : "hidden"}>
            <StoreFormSection
              title="Merchandising"
              help="Copy and attributes shown on the line sheet and the product page."
            >
              <StoreFormRow single>
                <TextField name="shortDesc" label="Short description (catalog teaser)" placeholder="Combed cotton, pre-shrunk" />
              </StoreFormRow>
              <StoreFormRow single>
                <FormField
                  control={form.control}
                  name="longDesc"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Long description (line sheet / storefront)</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          value={field.value ?? ""}
                          rows={4}
                          placeholder="Full marketing copy shown on the product page."
                          className={TEXTAREA_CLASS}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </StoreFormRow>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField name="brand" label="Brand" placeholder="Loomwell" />
                <SelectField
                  name="countryOfOrigin"
                  label="Country of origin"
                  options={COUNTRY_OPTIONS}
                  noneLabel="Not specified"
                />
                {/* The deferred Status control moved to the Review & Publish
                    step with #272 — it belongs beside the button it changes
                    the meaning of. */}
                <TextField name="material" label="Material / fabric" placeholder="100% cotton" />
                <TextField name="gsm" label="GSM" placeholder="180" />
                <TextField name="colour" label="Colour" placeholder="Ecru" />
                <SelectField
                  name="care"
                  label="Care"
                  options={CARE_OPTIONS}
                  noneLabel="Not specified"
                />
              </div>
            </StoreFormSection>
            </div>

            {step === 7 ? (
              <ProductReviewStep
                imagesCount={images.length}
                warehouseName={
                  warehouseOptions.find((o) => o.value === form.watch("openingWarehouseId"))
                    ?.label ?? null
                }
                createAs={createAs}
                onCreateAsChange={setCreateAs}
                errors={Object.entries(form.formState.errors).map(([field, err]) => ({
                  field,
                  message: (err as { message?: string })?.message ?? `Check ${field}`,
                }))}
                busy={busy}
                onJump={goToStep}
                onCreate={() => void submit(createAs === "published")}
              />
            ) : null}
          </div>

          {/* Wizard navigation. The header button stays available for an
              early draft save — nothing the single-page form allowed is lost. */}
          <div className="mt-4 flex items-center justify-between">
            <Button
              intent="secondary"
              size="md"
              type="button"
              disabled={step === 0}
              onClick={() => goToStep(step - 1)}
            >
              <ArrowLeft className="size-4" aria-hidden />
              Back
            </Button>
            {step < WIZARD_STEPS.length - 1 ? (
              <Button intent="primary" size="md" type="button" onClick={() => void nextStep()}>
                Next
                <ArrowRight className="size-4" aria-hidden />
              </Button>
            ) : null}
          </div>
            </div>

            <aside className="sticky top-4 hidden w-72 shrink-0 xl:block">
              <ProductPreviewRail images={images} createAs={createAs} />
            </aside>
          </div>
        </form>
      </Form>

      {optionDialog ? (
        <NewOptionDialog
          kind={optionDialog}
          open
          onOpenChange={(next) => !next && setOptionDialog(null)}
          // Sub-category duplicates are only duplicates under the same parent —
          // "Full sleeve" may exist under both Shirts and Jackets.
          existing={optionsForDialog(optionDialog, options, category).map((o) => o.value)}
          onCreate={({ name }) => {
            options.add(optionDialog, name, category);
            setPendingSelect({ kind: optionDialog, name });
            // No master table for either level — the value only exists once a
            // product carries it, so the toast says when it becomes permanent.
            toast.success(
              `${optionDialog === "subCategory" ? "Sub-category" : "Category"} “${name}” added`,
              { description: "Applied to this product. It is saved when you save the product." },
            );
          }}
        />
      ) : null}

      {/* Mounted only while open (fresh form each open). onCreated fires
          after the list invalidation, so the deferred-select effect above
          commits the id as soon as the refreshed options contain it. */}
      {warehouseDialogOpen ? (
        <WarehouseCreateDialog
          open
          onOpenChange={setWarehouseDialogOpen}
          onCreated={(id) => setPendingWarehouseId(id)}
        />
      ) : null}
    </StoreAdminLayout>
  );
}
